import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { ApiError, strategiesApi } from "@/lib/api";
import type { Strategy, StrategyCreateRequest, StrategyWithToken } from "@/types/strategy";
import type { UseToastResult } from "@/hooks/useToast";

export const STRATEGIES_KEY = ["strategies"] as const;

export interface TokenReveal {
  token: string;
  title: string;
  description: string;
}

interface Callbacks {
  onCreated: (s: StrategyWithToken) => void;
}

export interface StrategyActions {
  create: (req: StrategyCreateRequest) => void;
  remove: (id: string) => void;
  toggleKill: (id: string, killSwitch: boolean) => void;
  isPending: boolean;
}

interface Deps {
  invalidate: () => void;
  toast: Pick<UseToastResult, "push">;
  callbacks: Callbacks;
}

function onError(toast: Pick<UseToastResult, "push">, action: string) {
  return (err: unknown): void => {
    const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
    toast.push("error", `${action} failed: ${reason}`);
  };
}

function createOpts(
  d: Readonly<Deps>,
): UseMutationOptions<StrategyWithToken, unknown, StrategyCreateRequest> {
  return {
    mutationFn: (req) => strategiesApi.create(req),
    onSuccess: (s) => {
      d.invalidate();
      d.callbacks.onCreated(s);
      d.toast.push("success", "Strategy created");
    },
    onError: onError(d.toast, "Create"),
  };
}

function removeOpts(d: Readonly<Deps>): UseMutationOptions<void, unknown, string> {
  return {
    mutationFn: (id) => strategiesApi.remove(id),
    onSuccess: () => {
      d.invalidate();
      d.toast.push("success", "Strategy deleted");
    },
    onError: onError(d.toast, "Delete"),
  };
}

function patchOpts(
  d: Readonly<Deps>,
): UseMutationOptions<Strategy, unknown, { id: string; kill_switch: boolean }> {
  return {
    mutationFn: ({ id, kill_switch }) => strategiesApi.update(id, { kill_switch }),
    onSuccess: d.invalidate,
    onError: onError(d.toast, "Update"),
  };
}

export function useStrategyActions(
  toast: Readonly<Pick<UseToastResult, "push">>,
  callbacks: Readonly<Callbacks>,
): StrategyActions {
  const qc = useQueryClient();
  const deps: Deps = {
    invalidate: () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
    toast,
    callbacks,
  };
  const createMut = useMutation(createOpts(deps));
  const deleteMut = useMutation(removeOpts(deps));
  const patchMut = useMutation(patchOpts(deps));
  return {
    create: (req) => {
      createMut.mutate(req);
    },
    remove: (id) => {
      deleteMut.mutate(id);
    },
    toggleKill: (id, kill_switch) => {
      patchMut.mutate({ id, kill_switch });
    },
    isPending: createMut.isPending || deleteMut.isPending || patchMut.isPending,
  };
}
